import React from 'react';
import PageHeader from './PageHeader';
import AdminSidebar from './AdminSidebar';

/**
 * Shell variants — three different page structures share the same masthead
 * + soft-framed page wash, but the body chrome differs:
 *
 *   default — single white canvas with brand spine. Use for tabbed dashboards,
 *             list pages, and any view whose primary content is a single
 *             continuous surface (table, card grid, settings panel).
 *
 *   detail  — no canvas. Body content sits directly on the soft page wash so
 *             nested record panels (print-doc, profile blocks) carry their
 *             own chrome without "card-in-card" visual noise. Use for
 *             single-record views: Customer, Order, Product, OrderEdit,
 *             RemovalQuoteDetail.
 *
 *   form    — narrow centred column inside a canvas. Caps body width at
 *             ~720 px for legible single-column forms. Use for create/edit
 *             flows: UsersCreate, future onboarding pages.
 */
export type AdminPageShellVariant = 'default' | 'detail' | 'form';

interface Props {
  title: string;
  /** Optional one-line description rendered beneath the title. */
  subtitle?: string;
  onBack?: () => void;
  primaryAction?: React.ReactNode;
  /**
   * Page-scoped controls that affect every tab (e.g. partner picker,
   * brand selector, date range). Sits between the masthead header and
   * the canvas, visually attached to neither — it scopes the page.
   */
  contextBar?: React.ReactNode;
  /**
   * Tab strip rendered at the top edge of the canvas surface. Use the
   * <TabBar> primitive for the canonical chrome. Ignored for `detail` variant.
   */
  tabs?: React.ReactNode;
  /**
   * Banner / notice rendered above the canvas (errors, alerts). Pass an
   * <AdminNotice> here so it never floats inside the table area.
   */
  notice?: React.ReactNode;
  /** Page body — toolbar, table, cards, form. */
  children: React.ReactNode;
  /** Layout variant — see AdminPageShellVariant. Default `default`. */
  variant?: AdminPageShellVariant;
}

/**
 * Editorial admin page-shell. Single source of truth for the masthead +
 * canvas pattern shared across every admin route. Replaces the loose
 * `<PageHeader> + <admin-tab-bar> + <section>` composition that produced
 * the "everything floating in empty space" feel.
 */
export default function AdminPageShell({
  title,
  subtitle,
  onBack,
  primaryAction,
  contextBar,
  tabs,
  notice,
  children,
  variant = 'default',
}: Props) {
  const wrapperClass = ['admin-shell', `admin-shell--${variant}`].join(' ');

  return (
    <div className={wrapperClass}>
      <AdminSidebar />
      <div className="admin-shell__main">
        <div className="admin-shell__inner">
          <PageHeader
            title={title}
            subtitle={subtitle}
            onBack={onBack}
            primaryAction={primaryAction}
          />
          {contextBar && (
            <div className="admin-shell__context-bar">{contextBar}</div>
          )}
          {notice && <div className="admin-shell__notice">{notice}</div>}

          {variant === 'detail' ? (
            // No canvas — content cards float on the soft page wash.
            <div className="admin-shell__detail-body">{children}</div>
          ) : (
            <div className="admin-shell__canvas">
              {tabs && <div className="admin-shell__tabs">{tabs}</div>}
              <div
                className={
                  variant === 'form'
                    ? 'admin-shell__body admin-shell__body--form'
                    : 'admin-shell__body'
                }
              >
                {children}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
